import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faTriangleExclamation,
  faReceipt,
  faCircleCheck,
  faCircleHalfStroke,
  faCircleXmark,
  faGift,
  faScaleUnbalanced,
  faSackDollar,
  faHandHoldingDollar,
  faScaleBalanced,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';
import './Myearningslegacy.css'; // LEGACY: old global classes other old pages may use
import './MyEarnings.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const TABS = [
  ['summary', 'Summary'],
  ['paid', 'Paid sessions'],
  ['demo', 'Demo sessions'],
  ['incentive', 'Incentives'],
  ['penalties', 'Penalties'],
];

// Same pay codes as before
const PAY = {
  0: { label: 'Full pay', tone: 'full', icon: faCircleCheck, factor: 1 },
  1: { label: 'Half pay', tone: 'half', icon: faCircleHalfStroke, factor: 0.5 },
  2: { label: 'No pay', tone: 'none', icon: faCircleXmark, factor: 0 },
};
const payOf = (v) => PAY[v] || { label: 'Unknown', tone: 'none', icon: faCircleXmark, factor: 0 };

const num = (n) => Number(n) || 0;
const money = (n) => num(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const monthLabel = (y, m) => new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: '—', time: '' };
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
};

/* Same date the old page sent (today's day in the chosen month), from the LOCAL date
   (the old toISOString() used UTC and could slip to the day before after midnight) */
const requestDate = (y, m) => {
  const lastDay = new Date(y, m + 1, 0).getDate();
  const day = Math.min(new Date().getDate(), lastDay);
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/* Same props as before: { userData } */
const MyEarnings = ({ userData }) => {
  const now = new Date();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'summary';
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  const isCurrentMonth = ym.y === now.getFullYear() && ym.m === now.getMonth();

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'summary') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  const shiftMonth = (delta) => {
    setYm(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      const today = new Date();
      if (d.getFullYear() * 12 + d.getMonth() > today.getFullYear() * 12 + today.getMonth()) return { y, m }; // no future months
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!userData) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/earnings/${requestDate(ym.y, ym.m)}`, auth());
        if (!cancelled) setData(res.data || {});
      } catch (err) {
        console.error('Error fetching earnings:', err);
        if (!cancelled) {
          setData(null);
          setError('We couldn’t load the earnings for this month.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData, ym, reload]);

  // ---------- Derived ----------
  const paid = data?.paidDetails || [];
  const demos = data?.demoDetails || [];
  const extras = data?.incentiveData || [];
  const incentives = extras.filter((i) => num(i.Amount) > 0);
  const penalties = extras.filter((i) => num(i.Amount) < 0);
  const incentiveTotal = incentives.reduce((t, i) => t + num(i.Amount), 0);
  const penaltyTotal = penalties.reduce((t, i) => t + num(i.Amount), 0);
  // demo rate: from the server totals (the old page always showed $1.00)
  const demoRate = data?.demoCount ? num(data.demoPayTotal) / data.demoCount : 1;

  const revenue = num(data?.AdmintotalPay); // from parents
  const payout = num(data?.totalPay); // to teachers
  const net = revenue - payout; // same as before: Admin total − teachers total
  const margin = revenue > 0 ? Math.round((net / revenue) * 100) : null;

  // extra columns only when the server sends them
  const hasFees = paid.some((d) => d.SessionFees !== undefined && d.SessionFees !== null);
  const teacherOf = (d) => d.TeacherName || d.teachername || d.TeacherFullName || '';
  const hasTeacher = [...paid, ...demos, ...extras].some((d) => teacherOf(d));

  const counts = { paid: paid.length, demo: demos.length, incentive: incentives.length, penalties: penalties.length };
  const dash = loading || !data;

  /* ---------------- Tab content ---------------- */
  const renderSummary = () => {
    const rows = [
      { label: 'Demo sessions', count: data.demoCount || 0, teacher: data.demoPayTotal },
      { label: 'Paid classes · full pay', count: data.fullPayCount || 0, teacher: data.fullPayTotal, parents: data.AdminfullPayTotal },
      { label: 'Paid classes · half pay', count: data.halfPayCount || 0, teacher: data.halfPayTotal, parents: data.AdminhalfPayTotal },
      { label: 'Incentives', teacher: data.incentive },
      { label: 'Arrears', teacher: data.arrears },
      { label: 'Penalties', teacher: data.penalties, negative: true },
    ];
    return (
      <ul className="rmaer-list rmaer-list--summary">
        <li className="rmaer-row rmaer-row--head" aria-hidden="true">
          <span>Type</span>
          <span>Count</span>
          <span className="rmaer-amount">Teachers</span>
          <span className="rmaer-amount">Parents</span>
        </li>
        {rows.map((r) => (
          <li key={r.label} className={`rmaer-row ${r.negative ? 'is-negative' : ''}`}>
            <span className="rmaer-row-main">{r.label}</span>
            <span className="rmaer-row-count">{r.count != null ? `× ${r.count}` : ''}</span>
            <span className="rmaer-amount" data-label="Teachers">
              {money(r.teacher)}
            </span>
            <span className="rmaer-amount is-parents" data-label="Parents">
              {r.parents !== undefined ? money(r.parents) : <span className="rmaer-dim">—</span>}
            </span>
          </li>
        ))}
        <li className="rmaer-row is-total">
          <span className="rmaer-row-main">Total</span>
          <span className="rmaer-row-count" />
          <span className="rmaer-amount is-out" data-label="Teachers">
            {money(payout)}
          </span>
          <span className="rmaer-amount is-in" data-label="Parents">
            {money(revenue)}
          </span>
        </li>
        <li className={`rmaer-row is-total is-net ${net < 0 ? 'is-negative' : ''}`}>
          <span className="rmaer-row-main">Net for RoboMind</span>
          <span className="rmaer-row-count">{margin !== null ? `${margin}% of revenue` : ''}</span>
          <span className="rmaer-amount" />
          <span className="rmaer-amount" data-label="Net">
            {money(net)}
          </span>
        </li>
      </ul>
    );
  };

  const renderSessions = (list, kind) => {
    if (!list.length) {
      return (
        <div className="rmaer-empty">
          <FontAwesomeIcon icon={faReceipt} />
          <p>{kind === 'paid' ? 'No paid classes recorded this month.' : 'No demo sessions this month.'}</p>
        </div>
      );
    }
    const showFees = kind === 'paid' && hasFees;
    return (
      <ul className={`rmaer-list ${showFees ? 'has-fees' : ''}`}>
        <li className="rmaer-row rmaer-row--head rmaer-row--session" aria-hidden="true">
          <span>Student</span>
          <span>Date</span>
          <span>Rate</span>
          <span>Status</span>
          <span className="rmaer-amount">Teacher</span>
          {showFees && <span className="rmaer-amount">Parents</span>}
        </li>
        {list.map((d, i) => {
          const w = when(d.ClassDateTime);
          const pay = kind === 'paid' ? payOf(d.fullpay) : { label: 'Demo completed', tone: 'full', icon: faCircleCheck };
          const rate = kind === 'paid' ? num(d.SessionRate) : demoRate;
          const earned = kind === 'paid' ? rate * pay.factor : demoRate;
          const teacher = teacherOf(d);
          return (
            <li key={`${d.ClassDateTime}-${i}`} className="rmaer-row rmaer-row--session">
              <span className="rmaer-student">
                {d.fullname || 'Student'}
                {hasTeacher && teacher && <small>with {teacher}</small>}
              </span>
              <span className="rmaer-when">
                {w.day}
                {w.time && <small>{w.time}</small>}
              </span>
              <span className="rmaer-rate">
                <small>Rate</small> {money(rate)}
              </span>
              <span className={`rmaer-pill is-${pay.tone}`}>
                <FontAwesomeIcon icon={pay.icon} /> {pay.label}
              </span>
              <span className="rmaer-amount is-out">{money(earned)}</span>
              {showFees && <span className="rmaer-amount is-in">{money(d.SessionFees)}</span>}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderExtras = (list, kind) => {
    const total = kind === 'incentive' ? incentiveTotal : penaltyTotal;
    if (!list.length) {
      return (
        <div className="rmaer-empty">
          <FontAwesomeIcon icon={kind === 'incentive' ? faGift : faCircleCheck} className={kind === 'penalties' ? 'is-good' : ''} />
          <p>{kind === 'incentive' ? 'No incentives this month.' : 'No penalties this month.'}</p>
          <span>Add them from a teacher’s profile → Earnings.</span>
        </div>
      );
    }
    return (
      <ul className="rmaer-list">
        {list.map((item) => {
          const d = new Date(item.DateOfIncentive);
          const teacher = teacherOf(item);
          return (
            <li key={item.Id} className={`rmaer-row rmaer-row--extra ${kind === 'penalties' ? 'is-negative' : 'is-positive'}`}>
              <span className="rmaer-row-main">
                <FontAwesomeIcon icon={kind === 'incentive' ? faGift : faScaleUnbalanced} className="rmaer-row-icon" />
                <span>
                  {item.Title || (kind === 'incentive' ? 'Incentive pay' : 'Penalty')}
                  {teacher && <small>{teacher}</small>}
                </span>
              </span>
              <span className="rmaer-row-count">
                {Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span className="rmaer-amount">{money(item.Amount)}</span>
            </li>
          );
        })}
        <li className={`rmaer-row rmaer-row--extra is-total ${kind === 'penalties' ? 'is-negative' : ''}`}>
          <span className="rmaer-row-main">Total</span>
          <span className="rmaer-row-count" />
          <span className="rmaer-amount">{money(total)}</span>
        </li>
      </ul>
    );
  };

  const renderContent = () => {
    if (loading || !userData) {
      return (
        <div className="rmaer-list rmaer-list--loading" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rmaer-skel" />
          ))}
        </div>
      );
    }
    if (error || !data) {
      return (
        <div className="rmaer-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error || 'No data for this month.'}</p>
          <button type="button" className="rmaer-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      );
    }
    if (tab === 'paid') return renderSessions(paid, 'paid');
    if (tab === 'demo') return renderSessions(demos, 'demo');
    if (tab === 'incentive') return renderExtras(incentives, 'incentive');
    if (tab === 'penalties') return renderExtras(penalties, 'penalties');
    return renderSummary();
  };

  return (
    <div className="rmaer">
      {/* ---------- Title + month ---------- */}
      <div className="rmaer-head">
        <div>
          <h1>Earnings</h1>
          <p>What parents paid, what teachers earned, and what’s left.</p>
        </div>
        <div className="rmaer-month">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <strong>{monthLabel(ym.y, ym.m)}</strong>
          <button type="button" onClick={() => shiftMonth(1)} disabled={isCurrentMonth} aria-label="Next month">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* ---------- Totals ---------- */}
      <section className="rmaer-stats" aria-label={`Totals for ${monthLabel(ym.y, ym.m)}`}>
        <div className="rmaer-stat is-in">
          <span className="rmaer-stat-icon">
            <FontAwesomeIcon icon={faSackDollar} />
          </span>
          <span>
            <small>Revenue (parents)</small>
            <strong>{dash ? '—' : money(revenue)}</strong>
          </span>
        </div>
        <div className="rmaer-stat is-out">
          <span className="rmaer-stat-icon">
            <FontAwesomeIcon icon={faHandHoldingDollar} />
          </span>
          <span>
            <small>Teacher payout</small>
            <strong>{dash ? '—' : money(payout)}</strong>
          </span>
        </div>
        <div className={`rmaer-stat is-net ${!dash && net < 0 ? 'is-loss' : ''}`}>
          <span className="rmaer-stat-icon">
            <FontAwesomeIcon icon={faScaleBalanced} />
          </span>
          <span>
            <small>Net for RoboMind{!dash && margin !== null ? ` · ${margin}%` : ''}</small>
            <strong>{dash ? '—' : money(net)}</strong>
          </span>
        </div>
      </section>
      <ul className="rmaer-chips">
        <li>
          <b>{dash ? '–' : num(data.fullPayCount) + num(data.halfPayCount)}</b> paid classes
        </li>
        <li>
          <b>{dash ? '–' : num(data.demoCount)}</b> demos
        </li>
        <li className="is-positive">
          <b>{dash ? '–' : money(data.incentive)}</b> incentives
        </li>
        <li className="is-negative">
          <b>{dash ? '–' : money(data.penalties)}</b> penalties
        </li>
        {isCurrentMonth && <li className="rmaer-chip-note">Month in progress</li>}
      </ul>

      {/* ---------- Tabs ---------- */}
      <div className="rmaer-tabs" role="tablist" aria-label="Earnings sections">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={`rmaer-tab ${tab === key ? 'is-on' : ''}`} onClick={() => setTab(key)}>
            {label}
            {!loading && data && counts[key] > 0 && <span>{counts[key]}</span>}
          </button>
        ))}
      </div>

      <div className="rmaer-panel" role="tabpanel">
        {renderContent()}
      </div>
    </div>
  );
};

export default MyEarnings;